import { supabase } from '../lib/supabase';

/**
 * Service for managing Shakespeare plays in the database
 */

/**
 * Fetch all plays with enriched statistics for admin management
 * @returns {Promise<Array>} Array of play objects with statistics
 */
export const getAllPlays = async () => {
  try {
    const { data: playsData, error: playsError } = await supabase
      ?.from('plays')
      ?.select('*')
      ?.order('updated_at', { ascending: false });

    if (playsError) throw playsError;

    // Enrich each play with additional statistics
    const enrichedPlays = await Promise.all(
      playsData?.map(async (play) => {
        // Get active student count (users who have started this play)
        const { count: studentCount } = await supabase
          ?.from('user_progress')
          ?.select('*', { count: 'exact', head: true })
          ?.eq('play_id', play?.id);

        // Get audio files count - join with lines table to filter by play_id
        const { count: audioCount } = await supabase
          ?.from('audio_recordings')
          ?.select('line_id, lines!inner(play_id)', { count: 'exact', head: true })
          ?.eq('lines.play_id', play?.id);

        // Get glossary terms count
        const { count: glossaryCount } = await supabase
          ?.from('glossary_terms')
          ?.select('*', { count: 'exact', head: true })
          ?.eq('play_id', play?.id);

        const totalLines = play?.total_lines || 0;
        const audioCompletion = totalLines > 0 ? Math.round((audioCount / totalLines) * 100) : 0;
        const glossaryCompletion = totalLines > 0 ? Math.round((glossaryCount / totalLines) * 100) : 0;

        return {
          id: play?.id,
          title: play?.title,
          author: play?.author,
          description: play?.description,
          actCount: play?.total_acts || 0,
          sceneCount: play?.total_scenes || 0,
          lineCount: play?.total_lines || 0,
          characterCount: play?.total_characters || 0, // Use database value
          studentCount: studentCount || 0,
          audioFilesUploaded: audioCount || 0,
          audioCompletion: audioCompletion,
          glossaryTermsAssigned: glossaryCount || 0,
          glossaryCompletion: glossaryCompletion,
          uploadDate: play?.created_at,
          lastModified: play?.updated_at,
          isPublished: play?.is_published,
          coverImageUrl: play?.cover_image_url,
          difficulty: play?.difficulty,
          yearWritten: play?.year_written
        };
      })
    );

    return enrichedPlays;
  } catch (error) {
    console.error('Error fetching all plays:', error);
    throw error;
  }
};

/**
 * Create a new play
 * @param {Object} playData - Play data including title, author, description, etc.
 * @returns {Promise<Object>} Created play object
 */
export const createPlay = async (playData) => {
  try {
    const { data, error } = await supabase
      ?.from('plays')
      ?.insert([playData])
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating play:', error);
    throw error;
  }
};

/**
 * Create a complete play with scenes and lines from CSV data
 * @param {Object} playData - Basic play information
 * @param {Object} csvAnalysis - Analyzed CSV data with statistics
 * @param {string} userId - User ID creating the play
 * @returns {Promise<Object>} Created play with full data
 */
export const createPlayWithContent = async (playData, csvAnalysis, userId) => {
  try {
    // Step 1: Create the play record
    const { data: play, error: playError } = await supabase
      ?.from('plays')
      ?.insert([{
        ...playData,
        total_acts: csvAnalysis?.totalActs,
        total_scenes: csvAnalysis?.totalScenes,
        total_lines: csvAnalysis?.totalLines,
        created_by: userId
      }])
      ?.select()
      ?.single();

    if (playError) throw playError;

    // Step 2: Create scenes with upsert to handle re-uploads
    const scenesToInsert = csvAnalysis?.scenes?.map(scene => ({
      play_id: play?.id,
      act_number: scene?.act,
      scene_number: scene?.scene,
      title: scene?.title,
      description: scene?.description,
      line_count: scene?.lineCount
    }));

    const { data: insertedScenes, error: scenesError } = await supabase
      ?.from('scenes')
      ?.upsert(scenesToInsert, { 
        onConflict: 'play_id,act_number,scene_number',
        ignoreDuplicates: false 
      })
      ?.select();

    if (scenesError) throw scenesError;

    // Create a map of scene_key to scene_id for line insertion
    const sceneMap = new Map();
    insertedScenes?.forEach(scene => {
      const key = `${scene?.act_number}.${scene?.scene_number}`;
      sceneMap?.set(key, scene?.id);
    });

    // Step 3: Create characters from CSV data
    // Characters are identified from CHARACTER type rows (text column = character name)
    // Fall back to speaker column on LINE rows if no CHARACTER rows present
    const characterMap = new Map();
    
    // First pass: collect characters from CHARACTER type rows
    const hasCharacterRows = csvAnalysis?.lines?.some(line => line?.lineType === 'CHARACTER');
    
    csvAnalysis?.lines?.forEach(line => {
      if (hasCharacterRows) {
        // New CSV format: CHARACTER rows define the characters
        if (line?.lineType === 'CHARACTER' && line?.dialogue) {
          const charName = line?.dialogue?.trim();
          const charKey = charName?.toUpperCase();
          if (charKey && !characterMap?.has(charKey)) {
            characterMap?.set(charKey, {
              name: charName,
              lineCount: 0,
              sceneIds: new Set()
            });
          }
        }
        // Count actual spoken lines (LINE type) toward character line counts
        if (line?.lineType === 'LINE' && line?.character) {
          const charKey = line?.character?.toUpperCase()?.trim();
          if (!characterMap?.has(charKey)) {
            // Speaker appears in LINE rows but has no CHARACTER row — add them
            characterMap?.set(charKey, {
              name: line?.character?.trim(),
              lineCount: 0,
              sceneIds: new Set()
            });
          }
          const charData = characterMap?.get(charKey);
          charData.lineCount++;
          const sceneKey = `${line?.actNumber}.${line?.sceneNumber}`;
          const sceneId = sceneMap?.get(sceneKey);
          if (sceneId) {
            charData?.sceneIds?.add(sceneId);
          }
        }
      } else {
        // Legacy format: use speaker column on non-stage-direction rows
        if (line?.character && !line?.isStageDirection) {
          const charKey = line?.character?.toUpperCase()?.trim();
          if (!characterMap?.has(charKey)) {
            characterMap?.set(charKey, {
              name: line?.character?.trim(),
              lineCount: 0,
              sceneIds: new Set()
            });
          }
          const charData = characterMap?.get(charKey);
          charData.lineCount++;
          const sceneKey = `${line?.actNumber}.${line?.sceneNumber}`;
          const sceneId = sceneMap?.get(sceneKey);
          if (sceneId) {
            charData?.sceneIds?.add(sceneId);
          }
        }
      }
    });

    // Insert characters into characters table
    const charactersToInsert = Array.from(characterMap?.entries())?.map(([name, data]) => {
      let roleClassification = 'minor';
      if (data?.lineCount > 100) roleClassification = 'protagonist';
      else if (data?.lineCount > 50) roleClassification = 'antagonist';
      else if (data?.lineCount > 20) roleClassification = 'supporting';

      return {
        play_id: play?.id,
        name: name,
        line_count: data?.lineCount,
        scene_appearances: data?.sceneIds?.size,
        role_classification: roleClassification
      };
    });

    const { error: charactersError } = await supabase
      ?.from('characters')
      ?.upsert(charactersToInsert, {
        onConflict: 'play_id,name',
        ignoreDuplicates: false
      });

    if (charactersError) {
      console.error('Error inserting characters:', charactersError);
      throw charactersError;
    }

    // Step 4: Create lines in batches with deduplication and upsert
    const batchSize = 1000;
    
    // Deduplicate lines by gid to prevent constraint violations within the same batch
    const gidSet = new Set();
    const linesToInsert = [];
    
    csvAnalysis?.lines?.forEach(line => {
      const sceneKey = `${line?.actNumber}.${line?.sceneNumber}`;
      const sceneId = sceneMap?.get(sceneKey);
      
      // Create a unique key for this line (play_id + gid)
      const uniqueKey = `${play?.id}_${line?.gid}`;
      

      // Only add if we haven't seen this gid yet for this play
      if (!gidSet?.has(uniqueKey)) {
        gidSet?.add(uniqueKey);
        linesToInsert?.push({
          play_id: play?.id,
          scene_id: sceneId,
          gid: line?.gid,
          line_number: line?.lineNumber,
          act: line?.actNumber,
          scene: line?.sceneNumber,
          line_in_scene: line?.lineInScene,
          character: line?.character,
          dialogue: line?.dialogue,
          // Store the full line type string: STAGE_DIRECTION, CHARACTER, or LINE
          is_stage_direction: line?.lineType || (line?.isStageDirection ? 'STAGE_DIRECTION' : 'LINE'),
          context_notes: line?.contextNotes,
          pronunciation_help: line?.pronunciationHelp
        });
      }
    });

    // Insert lines in batches with upsert
    for (let i = 0; i < linesToInsert?.length; i += batchSize) {
      const batch = linesToInsert?.slice(i, i + batchSize);
      const { error: linesError } = await supabase
        ?.from('lines')
        ?.upsert(batch, {
          onConflict: 'play_id,gid',
          ignoreDuplicates: false
        });

      if (linesError) {
        console.error(`Error inserting batch ${Math.floor(i / batchSize) + 1}:`, linesError);
        throw linesError;
      }
    }

    // Return enriched play data with character count
    return {
      ...play,
      characterCount: csvAnalysis?.characterCount,
      scenes: insertedScenes,
      totalLinesInserted: linesToInsert?.length,
      duplicatesRemoved: csvAnalysis?.lines?.length - linesToInsert?.length
    };
  } catch (error) {
    console.error('Error creating play with content:', error);
    throw error;
  }
};

/**
 * Delete a play by ID
 * @param {string} playId - UUID of the play to delete
 * @returns {Promise<void>}
 */
export const deletePlay = async (playId) => {
  try {
    const { error } = await supabase
      ?.from('plays')
      ?.delete()
      ?.eq('id', playId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting play:', error);
    throw error;
  }
};

/**
 * Bulk delete plays
 * @param {Array<string>} playIds - Array of play UUIDs to delete
 * @returns {Promise<void>}
 */
export const bulkDeletePlays = async (playIds) => {
  try {
    const { error } = await supabase
      ?.from('plays')
      ?.delete()
      ?.in('id', playIds);

    if (error) throw error;
  } catch (error) {
    console.error('Error bulk deleting plays:', error);
    throw error;
  }
};

/**
 * Bulk publish/unpublish plays
 * @param {Array<string>} playIds - Array of play UUIDs
 * @param {boolean} isPublished - Whether to publish or unpublish
 * @returns {Promise<void>}
 */
export const bulkUpdatePlayStatus = async (playIds, isPublished) => {
  try {
    const { error } = await supabase
      ?.from('plays')
      ?.update({ is_published: isPublished })
      ?.in('id', playIds);

    if (error) throw error;
  } catch (error) {
    console.error('Error bulk updating play status:', error);
    throw error;
  }
};

/**
 * Fetch plays that should be displayed on the homepage
 * Filters for published plays
 * @returns {Promise<Array>} Array of play objects for homepage showcase
 */
export const getHomepagePlays = async () => {
  try {
    const { data, error } = await supabase?.from('plays')?.select('id, title, author, description, cover_image_url, difficulty, year_written')?.eq('is_published', true)?.order('title', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching homepage plays:', error);
    throw error;
  }
};

/**
 * Update a play's information
 * @param {string} playId - UUID of the play to update
 * @param {Object} updates - Fields to update
 * @param {string} userId - User ID making the update
 * @returns {Promise<Object>} Updated play object
 */
export const updatePlay = async (playId, updates, userId) => {
  try {
    const { data, error } = await supabase
      ?.from('plays')
      ?.update({
        ...updates,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', playId)
      ?.select();

    if (error) throw error;
    
    // Return first item from array or null if no rows updated
    return data?.[0] || null;
  } catch (error) {
    console.error('Error updating play:', error);
    throw error;
  }
};

/**
 * Update an existing play with new content from CSV while preserving audio associations
 * @param {string} playId - UUID of the existing play to update
 * @param {Object} csvAnalysis - Analyzed CSV data with statistics
 * @param {string} userId - User ID updating the play
 * @returns {Promise<Object>} Updated play with statistics
 */
export const updatePlayWithContent = async (playId, csvAnalysis, userId) => {
  try {
    // Step 1: Get existing play
    const { data: existingPlay, error: playError } = await supabase
      ?.from('plays')
      ?.select('*')
      ?.eq('id', playId)
      ?.single();

    if (playError) throw playError;

    // Step 2: Update play statistics
    const { data: updatedPlay, error: updateError } = await supabase
      ?.from('plays')
      ?.update({
        total_acts: csvAnalysis?.totalActs,
        total_scenes: csvAnalysis?.totalScenes,
        total_lines: csvAnalysis?.totalLines,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', playId)
      ?.select()
      ?.single();

    if (updateError) throw updateError;

    // Step 3: Get existing scenes for this play
    const { data: existingScenes, error: scenesQueryError } = await supabase
      ?.from('scenes')
      ?.select('id, act_number, scene_number')
      ?.eq('play_id', playId);

    if (scenesQueryError) throw scenesQueryError;

    // Create a map of existing scenes
    const existingSceneMap = new Map();
    existingScenes?.forEach(scene => {
      const key = `${scene?.act_number}.${scene?.scene_number}`;
      existingSceneMap?.set(key, scene?.id);
    });

    // Step 4: Insert only NEW scenes (scenes that don't exist yet)
    const newScenes = csvAnalysis?.scenes?.filter(scene => {
      const key = `${scene?.act}.${scene?.scene}`;
      return !existingSceneMap?.has(key);
    });

    let insertedScenes = [];
    if (newScenes?.length > 0) {
      const scenesToInsert = newScenes?.map(scene => ({
        play_id: playId,
        act_number: scene?.act,
        scene_number: scene?.scene,
        title: scene?.title,
        description: scene?.description,
        line_count: scene?.lineCount
      }));

      const { data: newInsertedScenes, error: scenesError } = await supabase
        ?.from('scenes')
        ?.insert(scenesToInsert)
        ?.select();

      if (scenesError) throw scenesError;
      insertedScenes = newInsertedScenes;

      // Add new scenes to the map
      newInsertedScenes?.forEach(scene => {
        const key = `${scene?.act_number}.${scene?.scene_number}`;
        existingSceneMap?.set(key, scene?.id);
      });
    }

    // Step 5: Get existing lines for this play to prevent duplicates
    const { data: existingLines, error: linesQueryError } = await supabase
      ?.from('lines')
      ?.select('gid')
      ?.eq('play_id', playId);

    if (linesQueryError) throw linesQueryError;

    // Create a set of existing gids
    const existingGids = new Set(existingLines?.map(line => line?.gid));

    // Step 6: Filter out duplicate lines - only insert lines that don't exist yet
    const newLines = [];
    let duplicatesSkipped = 0;

    csvAnalysis?.lines?.forEach(line => {
      const sceneKey = `${line?.actNumber}.${line?.sceneNumber}`;
      const sceneId = existingSceneMap?.get(sceneKey);

      // Skip if this gid already exists in the database
      if (existingGids?.has(line?.gid)) {
        duplicatesSkipped++;
        return;
      }

      // Only add new lines
      newLines?.push({
        play_id: playId,
        scene_id: sceneId,
        gid: line?.gid,
        line_number: line?.lineNumber,
        act: line?.actNumber,
        scene: line?.sceneNumber,
        line_in_scene: line?.lineInScene,
        character: line?.character,
        dialogue: line?.dialogue,
        // Store the full line type string: STAGE_DIRECTION, CHARACTER, or LINE
        is_stage_direction: line?.lineType || (line?.isStageDirection ? 'STAGE_DIRECTION' : 'LINE'),
        context_notes: line?.contextNotes,
        pronunciation_help: line?.pronunciationHelp
      });
    });

    // Step 7: Insert only NEW lines in batches
    const batchSize = 1000;
    let totalInserted = 0;

    for (let i = 0; i < newLines?.length; i += batchSize) {
      const batch = newLines?.slice(i, i + batchSize);
      const { error: linesError } = await supabase
        ?.from('lines')
        ?.insert(batch);

      if (linesError) {
        console.error(`Error inserting batch ${Math.floor(i / batchSize) + 1}:`, linesError);
        throw linesError;
      }
      totalInserted += batch?.length;
    }

    // Return statistics
    return {
      ...updatedPlay,
      characterCount: csvAnalysis?.characterCount,
      newScenesAdded: insertedScenes?.length,
      newLinesAdded: totalInserted,
      duplicatesSkipped: duplicatesSkipped,
      totalLinesInCSV: csvAnalysis?.lines?.length,
      audioFilesPreserved: true
    };
  } catch (error) {
    console.error('Error updating play with content:', error);
    throw error;
  }
};

/**
 * Check if a play exists by title
 * @param {string} title - Play title to search for
 * @returns {Promise<Object|null>} Play object if found, null otherwise
 */
export const findPlayByTitle = async (title) => {
  try {
    const { data, error } = await supabase
      ?.from('plays')
      ?.select('*')
      ?.ilike('title', title)
      ?.maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error finding play by title:', error);
    throw error;
  }
};

/**
 * Upload play cover image to Supabase Storage
 * @param {string} playId - UUID of the play
 * @param {File} file - Image file to upload
 * @returns {Promise<string>} Public URL of uploaded image
 */
export const uploadPlayImage = async (playId, file) => {
  try {
    // Generate unique filename with timestamp
    const timestamp = Date.now();
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${playId}/${timestamp}.${fileExt}`;

    // Upload to play-images bucket (public bucket)
    const { data: uploadData, error: uploadError } = await supabase?.storage?.from('play-images')?.upload(fileName, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: urlData } = supabase?.storage?.from('play-images')?.getPublicUrl(fileName);

    const publicUrl = urlData?.publicUrl;

    // Update play record with new image URL
    const { error: updateError } = await supabase?.from('plays')?.update({ cover_image_url: publicUrl })?.eq('id', playId);

    if (updateError) throw updateError;

    return publicUrl;
  } catch (error) {
    console.error('Error uploading play image:', error);
    throw error;
  }
};

/**
 * Delete play cover image from storage
 * @param {string} imageUrl - Full URL of the image to delete
 * @returns {Promise<void>}
 */
export const deletePlayCoverImage = async (imageUrl) => {
  try {
    // Extract file path from URL
    // URL format: https://{project}.supabase.co/storage/v1/object/public/play-images/covers/filename.jpg
    const urlParts = imageUrl?.split('/play-images/');
    if (urlParts?.length < 2) {
      throw new Error('Invalid image URL format');
    }
    const filePath = urlParts?.[1];

    const { error } = await supabase?.storage?.from('play-images')?.remove([filePath]);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting play image:', error);
    throw error;
  }
};

/**
 * Get a single play by ID
 * @param {string} playId - UUID of the play
 * @returns {Promise<Object>} Play object
 */
export const getPlayById = async (playId) => {
  try {
    const { data, error } = await supabase?.from('plays')?.select('*')?.eq('id', playId)?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching play:', error);
    throw error;
  }
};

/**
 * Get comprehensive play statistics including unique character count
 * @param {string} playId - UUID of the play
 * @returns {Promise<Object>} Play statistics
 */
export const getPlayStatistics = async (playId) => {
  try {
    // Get play basic info
    const { data: play, error: playError } = await supabase
      ?.from('plays')
      ?.select('*')
      ?.eq('id', playId)
      ?.single();

    if (playError) throw playError;
    
    // Get scenes count
    const { count: scenesCount } = await supabase
      ?.from('scenes')
      ?.select('*', { count: 'exact', head: true })
      ?.eq('play_id', playId);

    // Get active student count
    const { count: studentCount } = await supabase
      ?.from('user_progress')
      ?.select('*', { count: 'exact', head: true })
      ?.eq('play_id', playId);

    return {
      ...play,
      characterCount: play?.total_characters || 0, // Use database value
      sceneCount: scenesCount || 0,
      studentCount: studentCount || 0
    };
  } catch (error) {
    console.error('Error fetching play statistics:', error);
    throw error;
  }
};

/**
 * Fetch lines for a specific scene with audio information
 * @param {string} playId - Play ID
 * @param {number} actNumber - Act number
 * @param {number} sceneNumber - Scene number
 * @returns {Promise<Array>} Array of lines with audio URLs
 */
export const getSceneLines = async (playId, actNumber, sceneNumber) => {
  try {
    const { data: sceneData, error: sceneError } = await supabase
      ?.from('scenes')
      ?.select('id')
      ?.eq('play_id', playId)
      ?.eq('act_number', actNumber)
      ?.eq('scene_number', sceneNumber)
      ?.single();

    if (sceneError) throw sceneError;

    if (!sceneData) {
      throw new Error('Scene not found');
    }

    const { data: linesData, error: linesError } = await supabase
      ?.from('lines')
      ?.select(`
        id,
        gid,
        line_number,
        act,
        scene,
        line_in_scene,
        character,
        dialogue,
        stage_direction,
        is_stage_direction,
        pronunciation_help,
        context_notes
      `)
      ?.eq('scene_id', sceneData?.id)
      ?.order('line_number', { ascending: true });

    if (linesError) throw linesError;

    // Check for audio recordings for each line with voice actor info
    const linesWithAudio = await Promise.all(
      linesData?.map(async (line) => {
        try {
          // Try to get audio file from audio_recordings table with voice actor info
          const { data: audioRecording, error: audioError } = await supabase
            ?.from('audio_recordings')
            ?.select(`
              file_path,
              user_id,
              user_profiles!audio_recordings_user_id_fkey (
                id,
                full_name
              ),
              voice_actors!inner (
                id,
                stage_name,
                headshot_url
              )
            `)
            ?.eq('line_id', line?.id)
            ?.maybeSingle();

          if (audioRecording?.file_path) {
            try {
              const { getAudioFileUrl } = await import('./audioManagementService');
              const signedUrl = await getAudioFileUrl(audioRecording?.file_path, 3600); // 1 hour expiry
              return {
                ...line,
                hasAudio: true,
                audioUrl: signedUrl,
                voiceActor: audioRecording?.voice_actors ? {
                  id: audioRecording?.voice_actors?.id,
                  name: audioRecording?.voice_actors?.stage_name,
                  headshotUrl: audioRecording?.voice_actors?.headshot_url,
                  userId: audioRecording?.user_id
                } : null
              };
            } catch (urlError) {
              console.error('Error getting audio URL:', urlError);
              return {
                ...line,
                hasAudio: false,
                audioUrl: null,
                voiceActor: null
              };
            }
          }

          return {
            ...line,
            hasAudio: false,
            audioUrl: null,
            voiceActor: null
          };
        } catch (error) {
          console.error('Error checking audio for line:', error);
          return {
            ...line,
            hasAudio: false,
            audioUrl: null,
            voiceActor: null
          };
        }
      })
    );

    return linesWithAudio;
  } catch (error) {
    console.error('Error fetching scene lines:', error);
    throw error;
  }
};

/**
 * Fetch glossary terms for a play with pronunciation audio
 * @param {string} playId - Play ID
 * @returns {Promise<Array>} Array of glossary terms with audio URLs
 */
export const getPlayGlossary = async (playId) => {
  try {
    const { data, error } = await supabase
      ?.from('glossary_terms')
      ?.select('*')
      ?.eq('play_id', playId)
      ?.order('term', { ascending: true });

    if (error) throw error;

    // Fetch signed URLs for terms with pronunciation audio
    const termsWithSignedUrls = await Promise.all(
      data?.map(async (term) => {
        if (term?.audio_url) {
          try {
            const { getAudioFileUrl } = await import('./audioManagementService');
            const signedUrl = await getAudioFileUrl(term?.audio_url, 3600);
            return {
              id: term?.id,
              word: term?.term,
              definition: term?.definition,
              hasAudio: !!signedUrl,
              example: term?.example_usage || '',
              pronunciation: term?.pronunciation || null,
              audioUrl: signedUrl || null
            };
          } catch (error) {
            console.error(`Failed to get signed URL for term ${term?.term}:`, error);
            return {
              id: term?.id,
              word: term?.term,
              definition: term?.definition,
              hasAudio: false,
              example: term?.example_usage || '',
              pronunciation: term?.pronunciation || null,
              audioUrl: null
            };
          }
        }
        return {
          id: term?.id,
          word: term?.term,
          definition: term?.definition,
          hasAudio: false,
          example: term?.example_usage || '',
          pronunciation: term?.pronunciation || null,
          audioUrl: null
        };
      })
    );

    return termsWithSignedUrls || [];
  } catch (error) {
    console.error('Error fetching play glossary:', error);
    throw error;
  }
};

/**
 * Fetch complete play structure with acts and scenes
 * @param {string} playId - Play ID
 * @returns {Promise<Object>} Play with organized acts and scenes
 */
export const getPlayStructure = async (playId) => {
  try {
    // Fetch play details
    const { data: play, error: playError } = await supabase
      ?.from('plays')
      ?.select('*')
      ?.eq('id', playId)
      ?.single();

    if (playError) throw playError;

    // Fetch all scenes for this play
    const { data: scenesData, error: scenesError } = await supabase
      ?.from('scenes')
      ?.select('*')
      ?.eq('play_id', playId)
      ?.order('act_number', { ascending: true })
      ?.order('scene_number', { ascending: true });

    if (scenesError) throw scenesError;

    // Organize scenes by act
    const actsMap = new Map();
    
    scenesData?.forEach(scene => {
      if (!actsMap?.has(scene?.act_number)) {
        actsMap?.set(scene?.act_number, {
          actNumber: scene?.act_number,
          scenes: []
        });
      }
      
      actsMap?.get(scene?.act_number)?.scenes?.push({
        sceneNumber: scene?.scene_number,
        setting: scene?.description || scene?.title || `Scene ${scene?.scene_number}`,
        sceneId: scene?.id,
        lineCount: scene?.line_count || 0
      });
    });

    // Convert map to array
    const acts = Array.from(actsMap?.values());

    return {
      title: play?.title,
      author: play?.author,
      playId: play?.id,
      totalActs: play?.total_acts || 0,
      totalScenes: play?.total_scenes || 0,
      totalLines: play?.total_lines || 0,
      acts: acts
    };
  } catch (error) {
    console.error('Error fetching play structure:', error);
    throw error;
  }
};

/**
 * Fetch user bookmarks for a play
 * @param {string} userId - User ID
 * @param {string} playId - Play ID
 * @returns {Promise<Array>} Array of bookmarks
 */
export const getUserBookmarks = async (userId, playId) => {
  try {
    const { data, error } = await supabase
      ?.from('bookmarks')
      ?.select(`
        id,
        note,
        created_at,
        lines (
          id,
          gid,
          dialogue,
          scenes (
            act_number,
            scene_number
          )
        )
      `)
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    // Filter bookmarks for this play and format them
    const formattedBookmarks = data
      ?.filter(bookmark => bookmark?.lines?.scenes)
      ?.map(bookmark => ({
        id: bookmark?.id,
        act: bookmark?.lines?.scenes?.act_number,
        scene: bookmark?.lines?.scenes?.scene_number,
        lineId: bookmark?.lines?.id,
        linePreview: bookmark?.lines?.dialogue?.substring(0, 60) + '...',
        timestamp: new Date(bookmark?.created_at),
        note: bookmark?.note
      })) || [];

    return formattedBookmarks;
  } catch (error) {
    console.error('Error fetching user bookmarks:', error);
    throw error;
  }
};

/**
 * Create a bookmark for a line
 * @param {string} userId - User ID
 * @param {string} lineId - Line UUID
 * @param {string} note - Optional note
 * @returns {Promise<Object>} Created bookmark
 */
export const createBookmark = async (userId, lineId, note = '') => {
  try {
    const { data, error } = await supabase
      ?.from('bookmarks')
      ?.insert([{
        user_id: userId,
        line_id: lineId,
        note: note
      }])
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating bookmark:', error);
    throw error;
  }
};

/**
 * Delete a bookmark
 * @param {string} bookmarkId - Bookmark UUID
 * @returns {Promise<void>}
 */
export const deleteBookmark = async (bookmarkId) => {
  try {
    const { error } = await supabase
      ?.from('bookmarks')
      ?.delete()
      ?.eq('id', bookmarkId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting bookmark:', error);
    throw error;
  }
};

/**
 * Get a single line by ID with all related data
 * @param {string} lineId - UUID of the line
 * @returns {Promise<Object>} Line object with play, scene, and glossary data
 */
export const getLineById = async (lineId) => {
  try {
    // Fetch the line with play and scene information
    const { data: lineData, error: lineError } = await supabase
      ?.from('lines')
      ?.select(`
        *,
        play:plays(*),
        scene:scenes(*)
      `)
      ?.eq('id', lineId)
      ?.single();

    if (lineError) throw lineError;
    if (!lineData) throw new Error('Line not found');

    // Fetch glossary terms assigned to this line
    const { data: glossaryAssignments, error: glossaryError } = await supabase
      ?.from('line_glossary_assignments')
      ?.select(`
        id,
        glossary_term:glossary_terms(
          id,
          term,
          definition,
          pronunciation
        )
      `)
      ?.eq('line_id', lineId);

    if (glossaryError) throw glossaryError;

    // Fetch audio recording for this line
    const { data: audioData, error: audioError } = await supabase
      ?.from('audio_recordings')
      ?.select('*')
      ?.eq('line_id', lineId)
      ?.order('created_at', { ascending: false })
      ?.limit(1)
      ?.maybeSingle();

    if (audioError) throw audioError;

    // Get all glossary terms for the play (for assignment dropdown)
    const { data: allGlossaryTerms, error: allGlossaryError } = await supabase
      ?.from('glossary_terms')
      ?.select('*')
      ?.eq('play_id', lineData?.play_id)
      ?.order('term', { ascending: true });

    if (allGlossaryError) throw allGlossaryError;

    // Format the response
    return {
      id: lineData?.id,
      lineId: lineData?.id,
      gid: lineData?.gid,
      lineNumber: lineData?.line_number,
      act: lineData?.act ?? lineData?.scene?.act_number,
      scene: lineData?.scene_number ?? lineData?.scene?.scene_number,
      lineInScene: lineData?.line_in_scene,
      character: lineData?.character,
      dialogue: lineData?.dialogue,
      stageDirection: lineData?.stage_direction,
      isStageDirection: lineData?.is_stage_direction,
      pronunciationHelp: lineData?.pronunciation_help,
      contextNotes: lineData?.context_notes,
      playId: lineData?.play_id,
      playTitle: lineData?.play?.title,
      sceneId: lineData?.scene_id,
      assignedGlossaryTerms: glossaryAssignments?.map(assignment => ({
        id: assignment?.glossary_term?.id,
        word: assignment?.glossary_term?.term,
        definition: assignment?.glossary_term?.definition,
        pronunciation: assignment?.glossary_term?.pronunciation
      })) || [],
      audioRecording: audioData ? {
        id: audioData?.id,
        audioUrl: audioData?.audio_url,
        durationSeconds: audioData?.duration_seconds
      } : null,
      availableGlossaryTerms: allGlossaryTerms?.map(term => ({
        id: term?.id,
        word: term?.term,
        definition: term?.definition,
        pronunciation: term?.pronunciation
      })) || []
    };
  } catch (error) {
    console.error('Error fetching line by ID:', error);
    throw error;
  }
};

function getPlays(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getPlays is not implemented yet.', args);
  return null;
}

export { getPlays };

/**
 * Fetch student dashboard data including plays with progress
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Dashboard data with plays and progress
 */
export const getStudentDashboardData = async (userId) => {
  try {
    // Fetch all published plays
    const { data: playsData, error: playsError } = await supabase
      ?.from('plays')
      ?.select('*')
      ?.eq('is_published', true)
      ?.order('title', { ascending: true });

    if (playsError) throw playsError;

    // Fetch user progress for all plays
    const { data: progressData, error: progressError } = await supabase
      ?.from('user_progress')
      ?.select('*')
      ?.eq('user_id', userId);

    if (progressError) throw progressError;

    // Create a map of play_id to progress for quick lookup
    const progressMap = new Map();
    progressData?.forEach(progress => {
      progressMap?.set(progress?.play_id, progress);
    });

    // Enrich plays with user progress and quiz data
    const enrichedPlays = await Promise.all(
      playsData?.map(async (play) => {
        const progress = progressMap?.get(play?.id);
        
        // Get quiz attempts count for this play
        const { count: quizCount } = await supabase
          ?.from('quiz_attempts')
          ?.select('*', { count: 'exact', head: true })
          ?.eq('user_id', userId)
          ?.in('quiz_id', 
            await supabase
              ?.from('quizzes')
              ?.select('id')
              ?.eq('play_id', play?.id)
              ?.then(({ data }) => data?.map(q => q?.id) || [])
          );

        return {
          id: play?.id,
          title: play?.title,
          author: play?.author,
          description: play?.description,
          coverImage: play?.cover_image_url,
          coverImageAlt: `Cover image for ${play?.title} by ${play?.author}`,
          difficulty: play?.difficulty || 'medium',
          totalActs: play?.total_acts || 0,
          characterCount: play?.total_characters || 0,
          estimatedTime: `${Math.ceil((play?.total_lines || 0) / 800)}-${Math.ceil((play?.total_lines || 0) / 600)} hours`,
          completionPercentage: progress?.completion_percentage || 0,
          actsCompleted: progress ? Math.floor((progress?.completion_percentage || 0) / 20) : 0,
          quizzesCompleted: quizCount || 0,
          xpEarned: 0, // Will be calculated from achievements
          lastActivity: progress?.last_accessed_at ? 
            `Last accessed ${new Date(progress?.last_accessed_at)?.toLocaleDateString()}` : 
            'Not started',
          availableQuizTypes: ['Reading Comprehension', 'Pronunciation', 'Character Understanding'],
          earnedBadges: [],
          isNew: !progress,
          totalScenes: play?.total_scenes || 0,
          totalLines: play?.total_lines || 0
        };
      })
    );

    // Separate plays into recent and available
    const recentPlays = enrichedPlays?.filter(play => play?.completionPercentage > 0);
    const availablePlays = enrichedPlays;

    return {
      recentPlays,
      availablePlays
    };
  } catch (error) {
    console.error('Error fetching student dashboard data:', error);
    throw error;
  }
};

/**
 * Fetch student achievements and badges
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Achievements and badges data
 */
export const getStudentAchievements = async (userId) => {
  try {
    // Fetch user achievements with achievement details
    const { data: achievementsData, error: achievementsError } = await supabase
      ?.from('user_achievements')
      ?.select(`
        id,
        earned_at,
        achievements (
          id,
          name,
          description,
          achievement_type,
          xp_reward,
          badge_icon
        )
      `)
      ?.eq('user_id', userId)
      ?.order('earned_at', { ascending: false })
      ?.limit(5);

    if (achievementsError) throw achievementsError;

    // Format achievements
    const achievements = achievementsData?.map(item => ({
      id: item?.id,
      title: item?.achievements?.name,
      description: item?.achievements?.description,
      type: item?.achievements?.achievement_type,
      earnedDate: new Date(item?.earned_at)?.toLocaleDateString(),
      xpAwarded: item?.achievements?.xp_reward || 0
    })) || [];

    // Fetch all earned badges
    const { data: badgesData, error: badgesError } = await supabase
      ?.from('achievement_badges')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('earned_at', { ascending: false });

    if (badgesError) throw badgesError;

    // Format badges
    const badges = badgesData?.map(badge => ({
      id: badge?.id,
      name: badge?.badge_name,
      description: badge?.badge_description,
      type: badge?.badge_type,
      rarity: 'common', // You may want to add this to database
      earnedDate: new Date(badge?.earned_at)?.toLocaleDateString()
    })) || [];

    return {
      achievements,
      badges
    };
  } catch (error) {
    console.error('Error fetching student achievements:', error);
    throw error;
  }
};

/**
 * Fetch leaderboard data
 * @param {number} limit - Number of top users to fetch
 * @returns {Promise<Array>} Array of top users with XP and play completion
 */
export const getLeaderboard = async (limit = 10) => {
  try {
    // Fetch top users by XP
    const { data: usersData, error: usersError } = await supabase
      ?.from('user_profiles')
      ?.select('id, full_name, xp_points')
      ?.eq('role', 'student')
      ?.order('xp_points', { ascending: false })
      ?.limit(limit);

    if (usersError) throw usersError;

    // Enrich with play completion count
    const leaderboardData = await Promise.all(
      usersData?.map(async (user) => {
        const { count: playsCompleted } = await supabase
          ?.from('user_progress')
          ?.select('*', { count: 'exact', head: true })
          ?.eq('user_id', user?.id)
          ?.eq('status', 'completed');

        return {
          id: user?.id,
          name: user?.full_name,
          xp: user?.xp_points || 0,
          playsCompleted: playsCompleted || 0
        };
      })
    );

    return leaderboardData;
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    throw error;
  }
};

/**
 * Search plays by title, line content (partial phrase fuzzy search), character, or setting
 * @param {string} searchQuery - Search query string
 * @returns {Promise<Array>} Array of matching play IDs with match details
 */
export const searchPlaysAdvanced = async (searchQuery) => {
  try {
    if (!searchQuery || searchQuery?.trim() === '') {
      return [];
    }

    const query = searchQuery?.trim()?.toLowerCase();
    const matchingPlayIds = new Set();

    // Search in play titles
    const { data: titleMatches, error: titleError } = await supabase
      ?.from('plays')
      ?.select('id')
      ?.ilike('title', `%${query}%`);

    if (titleError) throw titleError;
    titleMatches?.forEach(play => matchingPlayIds?.add(play?.id));

    // Search in line dialogue (partial phrase fuzzy search)
    const { data: dialogueMatches, error: dialogueError } = await supabase
      ?.from('lines')
      ?.select('play_id')
      ?.ilike('dialogue', `%${query}%`)
      ?.limit(1000); // Limit to prevent performance issues

    if (dialogueError) throw dialogueError;
    dialogueMatches?.forEach(line => matchingPlayIds?.add(line?.play_id));

    // Search in character names
    const { data: characterMatches, error: characterError } = await supabase
      ?.from('lines')
      ?.select('play_id')
      ?.ilike('character', `%${query}%`)
      ?.limit(1000);

    if (characterError) throw characterError;
    characterMatches?.forEach(line => matchingPlayIds?.add(line?.play_id));

    // Search in stage directions (settings)
    const { data: stageMatches, error: stageError } = await supabase
      ?.from('lines')
      ?.select('play_id')
      ?.ilike('stage_direction', `%${query}%`)
      ?.limit(1000);

    if (stageError) throw stageError;
    stageMatches?.forEach(line => matchingPlayIds?.add(line?.play_id));

    return Array.from(matchingPlayIds);
  } catch (error) {
    console.error('Error searching plays:', error);
    throw error;
  }
};
function uploadPlayCoverImage(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: uploadPlayCoverImage is not implemented yet.', args);
  return null;
}

export { uploadPlayCoverImage };

/**
 * Claim the daily XP bonus (+10 XP) for a user.
 * Checks if the bonus was already claimed today using last_daily_bonus_at column.
 * If not claimed today, increments xp_points by 10 and records the timestamp.
 *
 * @param {string} userId - Authenticated user ID
 * @returns {Promise<{claimed: boolean, newXp: number, currentXp: number}>}
 */
export const claimDailyXpBonus = async (userId) => {
  try {
    // Fetch current user profile
    const { data: profile, error: fetchError } = await supabase
      ?.from('user_profiles')
      ?.select('xp_points, last_daily_bonus_at')
      ?.eq('id', userId)
      ?.single();

    if (fetchError) throw fetchError;

    const currentXp = profile?.xp_points || 0;
    const lastBonus = profile?.last_daily_bonus_at;

    // Check if already claimed today
    if (lastBonus) {
      const lastBonusDate = new Date(lastBonus)?.toDateString();
      const today = new Date()?.toDateString();
      if (lastBonusDate === today) {
        return { claimed: false, currentXp };
      }
    }

    // Award +10 XP and record today's date
    const newXp = currentXp + 10;
    const { error: updateError } = await supabase
      ?.from('user_profiles')
      ?.update({
        xp_points: newXp,
        last_daily_bonus_at: new Date()?.toISOString()
      })
      ?.eq('id', userId);

    if (updateError) throw updateError;

    // Check XP milestone badges after daily bonus
    checkXpMilestoneBadgesLocal(userId, newXp)?.catch(err =>
      console.error('XP milestone badge check error:', err)
    );

    return { claimed: true, newXp };
  } catch (error) {
    console.error('Error claiming daily XP bonus:', error);
    return { claimed: false, currentXp: 0 };
  }
};

/**
 * Award +100 XP when a user starts a play for the first time.
 * Idempotent — guarded by play_start_xp_awarded on user_progress.
 *
 * @param {string} userId
 * @param {string} progressRowId - The user_progress row id
 * @returns {Promise<number>} New XP total
 */
export const awardPlayStartXP = async (userId, progressRowId) => {
  try {
    const { data: profile, error: fetchError } = await supabase
      ?.from('user_profiles')
      ?.select('xp_points')
      ?.eq('id', userId)
      ?.single();

    if (fetchError) throw fetchError;

    const currentXP = profile?.xp_points || 0;
    const newXP = currentXP + 100;
    const newLevel = Math.floor(newXP / 100) + 1;

    // Update XP on user_profiles
    const { error: xpError } = await supabase
      ?.from('user_profiles')
      ?.update({ xp_points: newXP, level: newLevel, updated_at: new Date()?.toISOString() })
      ?.eq('id', userId);

    if (xpError) throw xpError;

    // Mark the progress row so we never award again
    await supabase
      ?.from('user_progress')
      ?.update({ play_start_xp_awarded: true })
      ?.eq('id', progressRowId);

    // Award "First Act" badge (first play started)
    await awardBadgeIfNotExistsLocal(userId, 'play', 'First Act', 'Started your first Shakespeare play');

    // Check XP milestone badges
    await checkXpMilestoneBadgesLocal(userId, newXP);

    return newXP;
  } catch (err) {
    console.error('Error awarding play-start XP:', err);
    return 0;
  }
};

/**
 * Local helper — award a badge via the award_achievement_badge RPC.
 */
const awardBadgeIfNotExistsLocal = async (userId, badgeType, badgeName, badgeDescription) => {
  try {
    await supabase?.rpc('award_achievement_badge', {
      p_user_id: userId,
      p_badge_type: badgeType,
      p_badge_name: badgeName,
      p_badge_description: badgeDescription,
      p_goal_id: null,
    });
  } catch (err) {
    console.error('Badge award error:', err);
  }
};

/**
 * Local helper — check XP milestones and award badges.
 */
const checkXpMilestoneBadgesLocal = async (userId, newXp) => {
  const milestones = [
    { xp: 100,  type: 'xp', name: 'Century Scholar',  desc: 'Earned 100 XP' },
    { xp: 250,  type: 'xp', name: 'Rising Star',       desc: 'Earned 250 XP' },
    { xp: 500,  type: 'xp', name: 'Stage Veteran',     desc: 'Earned 500 XP' },
    { xp: 1000, type: 'xp', name: "Bard\'s Champion",   desc: 'Earned 1000 XP' },
  ];
  for (const m of milestones) {
    if (newXp >= m?.xp) {
      await awardBadgeIfNotExistsLocal(userId, m?.type, m?.name, m?.desc);
    }
  }
};

/**
 * Upsert user progress when a line is clicked in the play-reading-interface.
 * Increments lines_completed, updates last_line_read, recalculates
 * completion_percentage, and sets status to 'in_progress' or 'completed'.
 * On first start (new row), awards +100 XP and a "First Act" badge.
 *
 * @param {string} userId - Authenticated user ID
 * @param {string} playId - Play UUID
 * @param {number} lineNumber - The line_in_scene number of the clicked line
 * @param {number} totalLines - Total lines in the play (from play.total_lines)
 * @returns {Promise<Object>} Updated user_progress row
 */
export const upsertUserProgress = async (userId, playId, lineNumber, totalLines) => {
  try {
    // Fetch existing progress row (if any)
    const { data: existing, error: fetchError } = await supabase
      ?.from('user_progress')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.eq('play_id', playId)
      ?.maybeSingle();

    if (fetchError) throw fetchError;

    const isNewPlay = !existing;
    const newLinesCompleted = (existing?.lines_completed || 0) + 1;
    const completionPct = totalLines > 0
      ? Math.min(100, Math.round((newLinesCompleted / totalLines) * 100))
      : 0;
    const status = completionPct >= 100 ? 'completed' : 'in_progress';
    const now = new Date()?.toISOString();

    const upsertPayload = {
      user_id: userId,
      play_id: playId,
      lines_completed: newLinesCompleted,
      last_line_read: lineNumber,
      completion_percentage: completionPct,
      status,
      last_accessed_at: now,
      ...(isNewPlay ? { started_at: now, play_start_xp_awarded: false } : {})
    };

    const { data, error: upsertError } = await supabase
      ?.from('user_progress')
      ?.upsert(upsertPayload, { onConflict: 'user_id,play_id' })
      ?.select()
      ?.single();

    if (upsertError) throw upsertError;

    // Award play-start XP (+100) once per play, on first line click
    if (isNewPlay && data?.id && !data?.play_start_xp_awarded) {
      awardPlayStartXP(userId, data?.id)?.catch(err =>
        console.error('Play-start XP award error:', err)
      );
    }

    return data;
  } catch (error) {
    console.error('Error upserting user progress:', error);
    throw error;
  }
};